import { Autocomplete, Box, InputAdornment, TextField } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { countries } from "../../helpers/country-names.js";

export default function CountrySelect({ textFieldProps, field, disabled = false }) {
  const { i18n } = useTranslation(["common", "validation", "user"]);
  const locale = i18n.language.substring(0, 2)
  const countriesOrdered = useMemo(() => supportedCountries
      .sort(
        (a, b) => {
          const aName = countries.getName(a, locale);
          const bName = countries.getName(b, locale);
          return (aName > bName) ? 1 : ((bName > aName) ? -1 : 0);
        }),
    [locale]);
  return (
    <Autocomplete
      options={countriesOrdered}
      autoHighlight
      fullWidth
      getOptionLabel={(option) => countries.getName(option, locale) || option}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <img
            loading="lazy"
            width="20"
            src={`https://flagcdn.com/w20/${option.toLowerCase()}.png`}
            srcSet={`https://flagcdn.com/w40/${option.toLowerCase()}.png 2x`}
            alt=""
          />&nbsp;
          {countries.getName(option, locale) || option}
        </Box>
      )}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          slotProps={{
            htmlInput: {
              autoComplete: "new-password", // disable autocomplete and autofill,
              ...params.inputProps
            },
            input: {
              ...params.InputProps,
              startAdornment: (
                (field.value && <InputAdornment position="start" sx={{
                  marginLeft: "10px",
                  marginRight: "0",
                  "&.MuiInputAdornment-positionStart.MuiInputAdornment-root:not(.MuiInputAdornment-hiddenLabel)": {
                    marginTop: 0
                  }
                }}>
                  <img
                    loading="lazy"
                    width="20"
                    src={`https://flagcdn.com/w20/${field.value.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w40/${field.value.toLowerCase()}.png 2x`}
                    alt=""
                  />
                </InputAdornment>)
              )
            }
          }}
          {...textFieldProps}
        />
      )}
      sx={{
        "&.MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input": {
          padding: "0 10px;"
        },
        "&.MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
          padding: "2px"
        }
      }}
      {...field}
      onChange={(_, data) => {
        field.onChange(data);
      }}
    />
  );
}

export const supportedCountries = [
  "AD",
  "AE",
  "AF",
  "AG",
  "AI",
  "AL",
  "AM",
  "AO",
  "AQ",
  "AR",
  "AS",
  "AT",
  "AU",
  "AW",
  "AX",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BL",
  "BM",
  "BN",
  "BO",
  "BQ",
  "BR",
  "BS",
  "BT",
  "BW",
  "BY",
  "BZ",
  "CA",
  "CC",
  "CD",
  "CF",
  "CG",
  "CH",
  "CI",
  "CK",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CW",
  "CX",
  "CY",
  "CZ",
  "DE",
  "DJ",
  "DK",
  "DM",
  "DO",
  "DZ",
  "EC",
  "EE",
  "EG",
  "EH",
  "ER",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FK",
  "FM",
  "FO",
  "FR",
  "GA",
  "GB",
  "GD",
  "GE",
  "GF",
  "GG",
  "GH",
  "GI",
  "GL",
  "GM",
  "GN",
  "GP",
  "GQ",
  "GR",
  "GS",
  "GT",
  "GU",
  "GW",
  "GY",
  "HK",
  "HN",
  "HR",
  "HT",
  "HU",
  "IC",
  "ID",
  "IE",
  "IL",
  "IM",
  "IN",
  "IO",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JE",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KI",
  "KM",
  "KN",
  "KP",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LA",
  "LB",
  "LC",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LY",
  "MA",
  "MC",
  "MD",
  "ME",
  "MF",
  "MG",
  "MH",
  "MK",
  "ML",
  "MM",
  "MN",
  "MO",
  "MP",
  "MQ",
  "MR",
  "MS",
  "MT",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NC",
  "NE",
  "NF",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NR",
  "NU",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PF",
  "PG",
  "PH",
  "PK",
  "PL",
  "PM",
  "PN",
  "PR",
  "PS",
  "PT",
  "PW",
  "PY",
  "QA",
  "RE",
  "RO",
  "RS",
  "RU",
  "RW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SG",
  "SH",
  "SI",
  "SJ",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SR",
  "SS",
  "ST",
  "SV",
  "SX",
  "SY",
  "SZ",
  "TC",
  "TD",
  "TF",
  "TG",
  "TH",
  "TJ",
  "TK",
  "TL",
  "TM",
  "TN",
  "TO",
  "TR",
  "TT",
  "TV",
  "TW",
  "TZ",
  "UA",
  "UG",
  "UM",
  "US",
  "UY",
  "UZ",
  "VA",
  "VC",
  "VE",
  "VG",
  "VI",
  "VN",
  "VU",
  "WF",
  "WS",
  "XK",
  "YE",
  "YT",
  "ZA",
  "ZM",
  "ZW",
];
